// Loan dashboard — per-discipline health scorecard (Overview, above the
// fold). Eight lights from the server-computed health payload; click
// deep-links to the discipline's tab. All-green renders a single calm
// "All clear" state (a feature, not an afterthought). Severity comes from
// LoanHealthService — the same source as the tab badges and the feed.

import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Card, Skeleton } from "@/design-system/components/ui";
import type { LoanHealthDto } from "@/api/portfolioAdmin/loanHealthApi";
import {
  DISCIPLINE_LABEL,
  SEVERITY_DOT_CLASS,
} from "./healthDisplay";

const TARGET_TAB: Record<string, string> = {
  payments: "payment-tracking",
  insurance: "insurance",
  ucc: "ucc",
  covenants: "legal-review",
  reporting: "financial-ops",
  vendors: "vendors",
  qa: "legal-ops",
  defaults: "defaults",
};

interface HealthScorecardCardProps {
  loanId: string;
  health: LoanHealthDto | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function HealthScorecardCard({
  loanId,
  health,
  isLoading,
  isError,
}: HealthScorecardCardProps) {
  return (
    <Card id="health-scorecard" data-testid="loan-health-scorecard">
      <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-2">
        Health
      </h3>

      {isLoading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-space-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      )}

      {isError && !isLoading && (
        <p className="text-small text-status-error m-0" data-testid="scorecard-error">
          Could not load loan health. Refresh to retry.
        </p>
      )}

      {!isLoading && !isError && health && (
        health.disciplines.every((d) => d.severity === "ok") ? (
          <AllClear health={health} />
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-space-2"
            data-testid="scorecard-grid"
          >
            {health.disciplines.map((d) => (
              <Link
                key={d.discipline}
                to={`/portfolio-admin/loans/${loanId}?tab=${TARGET_TAB[d.discipline] ?? "overview"}`}
                className="flex flex-col gap-space-1 rounded-md border border-border-light bg-white-pure px-space-2 py-space-2 hover:bg-white-warm no-underline"
                data-testid={`scorecard-light-${d.discipline}`}
              >
                <span className="flex items-center gap-space-1">
                  <span
                    aria-hidden="true"
                    className={`inline-block h-2 w-2 rounded-full shrink-0 ${SEVERITY_DOT_CLASS[d.severity]}`}
                    data-testid={`scorecard-dot-${d.discipline}-${d.severity}`}
                  />
                  <span className="text-caption font-semibold uppercase tracking-label text-onyx">
                    {DISCIPLINE_LABEL[d.discipline]}
                  </span>
                  {d.count > 0 && (
                    <span className="text-caption tabular-nums text-gray-mid ml-auto">
                      {d.count}
                    </span>
                  )}
                </span>
                <span className="text-caption text-gray-mid truncate" title={d.headline}>
                  {d.severity === "unavailable" ? "Not available" : d.headline}
                </span>
              </Link>
            ))}
          </div>
        )
      )}
    </Card>
  );
}

/** Calm all-green state: one line + muted per-discipline ticks. */
function AllClear({ health }: { health: LoanHealthDto }) {
  return (
    <div data-testid="scorecard-all-clear">
      <p className="flex items-center gap-space-2 text-small text-status-success font-semibold m-0">
        <Check size={16} aria-hidden="true" />
        All clear — nothing needs attention.
      </p>
      <div className="flex flex-wrap gap-x-space-3 gap-y-space-1 mt-space-2">
        {health.disciplines.map((d) => (
          <span
            key={d.discipline}
            className="flex items-center gap-space-1 text-caption text-gray-mid"
          >
            <Check size={12} aria-hidden="true" />
            {DISCIPLINE_LABEL[d.discipline]}
          </span>
        ))}
      </div>
    </div>
  );
}
